import { DropdownOption } from 'components/final-form/dropdownUtils';
import { LocationDto, LocationSearchRequest, TariffDto, UpsertLocationDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from "react-message-source";
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Dimmer, Loader } from 'semantic-ui-react';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import AddButton from 'components/AddButton';
import { deleteLocationById, findLocationById, searchLocations } from 'services/locationServices';
import UpsertLocationForm from 'components/settings/location/UpsertLocationForm';
import { getAllTariffs } from 'services/tariffServices';
import _, { debounce } from 'lodash';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { errorUtils } from 'utils/errorUtils';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Button, Grid, GridColumn, GridRow, Modal } from 'semantic-ui-react';
import PxGrid from 'components/layout/PxGrid';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { FormState } from 'final-form';

interface Props extends MessageSourceProps {
}

const initialLocationsSearch: Partial<LocationSearchRequest> = {

  includeInactive: false

};

interface ProductFieldProps extends FormState {
  values: Partial<LocationSearchRequest>
}

interface State {
  errorMessage?: string;
  locationsLoaded: boolean;
  location?: UpsertLocationDto,
  locationId?: number,
  addEditModalOpen: boolean,
  foundLocation: Array<LocationDto>,
  cancelTokenSource: CancelTokenSource,
  tariffOptions: DropdownOption[],
  tariffs: TariffDto[],
  tariffMap: { [id: number]: TariffDto },
  searchValues: Partial<LocationSearchRequest>,
  includeInactive: boolean,
}

class LocationView extends Component<Props, State> {

  private mounted: boolean = false;
  initialValues: Partial<LocationSearchRequest> = {}

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      locationsLoaded: false,
      foundLocation: [],
      addEditModalOpen: false,
      cancelTokenSource,
      tariffs: [],
      tariffOptions: [],
      tariffMap: [],
      includeInactive: false,
      searchValues: this.initialValues
    }

    this.fetchPageData();
    this.onSearchLocations();
  }

  componentDidMount() {
    this.mounted = true;
  }

  fetchPageData = async () => {

    const {cancelTokenSource} = this.state;

    getAllTariffs(cancelTokenSource)
        .then(response => {
          const tariffOptions = response.map(t => {
            return {
              key: t.id,
              text: t.description,
              value: t.id
            }
          });

          const tariffMap = _.keyBy(response, "id");

          this.setState({
            tariffs: response,
            tariffOptions: tariffOptions,
            tariffMap
          })
        }).finally(() => {
      this.setState({
        locationsLoaded: true
      });

    });

  };

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    });
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [errorUtils.deletingReferencedLocationNotAllowed];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findLocationById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);

    }
  };

  showModal = (id?: number, location?: UpsertLocationDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        location: location,
        locationId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      location: undefined,
      locationId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    this.onSearchLocations();
    this.hideModal();
  };

  deleteLocation = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteLocationById(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundLocation}: State) => ({
            foundLocation: foundLocation
                .filter((location: LocationDto) => location.id !== id)
          }))
        }).catch((error) => this.handleError(error.response.data));
    this.onSearchLocations();
  };

  handleChange = debounce(({values}: ProductFieldProps) => {
    if (!this.mounted) {
      return;
    }

    this.setState({
          searchValues: values
        },
        () => {
          this.onSearchLocations()
        });

  }, 300);


  onSearchLocations = async () => {
    const {searchValues, cancelTokenSource} = this.state;

    this.setState({
      locationsLoaded: false
    })

    const onFinally = () => {
      this.setState({
        locationsLoaded: true
      });
    }

    searchLocations(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundLocation: response
          });
        })
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);

    this.hideModal();

  }


  render(): React.ReactNode {
    const {locationsLoaded, errorMessage} = this.state;

    return (
        <PraxStyledSegment>
          {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }

          <div>
            {this.renderSearchLocationSegment()}
          </div>

          <StyledViewTableRow>
            {!locationsLoaded ? this.renderLoader() : this.renderLocationSegment()}
          </StyledViewTableRow>

          {this.renderUpsertLocationForm()}

        </PraxStyledSegment>
    );
  }

  locationRowGetter = ({index}: any) => {
    const {foundLocation} = this.state;

    Object.assign(foundLocation[index], {index: index + 1});

    return foundLocation[index]
  };

  foundLocationRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundLocation} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundLocation[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderSearchLocationSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={this.onSearchLocations}
            initialValues={initialLocationsSearch}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    )
  };

  renderSearchForm = ({handleSubmit, submitting}: FormRenderProps): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
          <PxGrid>
            <GridRow>
              <GridColumn width={13}>
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn width={3} verticalAlign={'middle'}>
                {getMessage("location.search.description")}:
              </GridColumn>
              <GridColumn width={13}>
                <Field
                    name={"description"}
                    component={Input}
                    fluid
                />
              </GridColumn>
            </GridRow>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
              </Grid.Column>
              <Grid.Column width={3} verticalAlign="middle">
                <Field
                    name="includeInactive"
                    component={CheckBox}
                    label={getMessage("includingInactive")}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={10}>
                <AddButton
                    type="button"
                    buttonMessage={getMessage("button.add")}
                    onClick={() => this.showModal()}
                />
              </Grid.Column>
            </Grid.Row>
          </PxGrid>
        </form>
    );
  }

  renderLocationSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundLocation} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundLocation.length}
            rowGetter={this.locationRowGetter}
            rowRenderer={this.foundLocationRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 300,
                label: "#",
                dataKey: "index"
              },
              {
                width: 500,
                flexGrow: 1,
                label: (getMessage("location.description")),
                dataKey: "description"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("location.active")
    }
    return getMessage("location.inactive");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("location.confirmDelete")}
              onConfirm={() => this.deleteLocation(cellData)}
          />
        </React.Fragment>
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("location.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertLocationForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, location, locationId, tariffOptions, tariffMap} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("location.upsertLocationForm.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertLocationForm
                location={location}
                locationId={locationId}
                tariffOptions={tariffOptions}
                tariffMap={tariffMap}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }
}

export default withMessages(LocationView);
